/**
 * Clinic workspace: create, join, invite colleagues, members.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { OaWorkspaceShell } from '../components/OaWorkspaceShell';
import {
  registerClinic,
  joinClinic,
  loginClinic,
  requestClinicMagicLink,
  consumeClinicMagicLink,
  requestClinicPasswordReset,
  resetClinicPassword,
  getClinicMe,
  getClinicSession,
  setClinicSession,
  setClinicId,
  logoutClinic,
  fetchClinicAnalytics,
  lookupClinicMemberships,
  rememberClinicDemoProfile,
  listClinicDemoProfiles,
  switchClinicDemoProfile,
  refreshClinicSession,
  type ClinicDemoProfile,
} from '../api/client';
import { ClinicPracticeHome } from '../components/ClinicPracticeHome';
import { trackOncoAgentCTA, trackOncoAgentPageView } from '../utils/analytics';
import { getErrorMessage } from '../utils/errorHelpers';
import {
  clinicJoinIssues,
  clinicLoginIssues,
  clinicRegisterIssues,
  firstIssue,
} from '../clinicForm';

export function OncoAgentClinic(): React.ReactElement {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [mode, setMode] = useState<'login' | 'register' | 'join' | 'forgot' | 'reset'>('register');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [mfaNeeded, setMfaNeeded] = useState(false);
  const [apiKeyOnce, setApiKeyOnce] = useState<string | null>(null);
  const [me, setMe] = useState<any>(null);
  const [analytics, setAnalytics] = useState<any>(null);
  const [login, setLogin] = useState({ email: '', password: '', clinic_id: '', mfa_code: '' });
  const [memberships, setMemberships] = useState<
    Array<{ clinic_id: string; clinic_name?: string; role?: string }>
  >([]);
  const [newPassword, setNewPassword] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [resetNote, setResetNote] = useState<string | null>(null);
  const [magicNote, setMagicNote] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteNote, setInviteNote] = useState<string | null>(null);
  const [demoProfiles, setDemoProfiles] = useState<ClinicDemoProfile[]>([]);

  const [reg, setReg] = useState({
    name: '',
    admin_name: '',
    admin_email: '',
    password: '',
    city: '',
    state: '',
    mode: 'both' as 'referring' | 'receiving' | 'both',
    network_id: '',
  });
  const [join, setJoin] = useState({
    invite_code: '',
    name: '',
    email: '',
    role: 'clinician',
    password: '',
  });

  const refresh = async () => {
    if (!getClinicSession()) {
      setMe(null);
      setAnalytics(null);
      setDemoProfiles(listClinicDemoProfiles());
      return;
    }
    try {
      try {
        const refreshed = await refreshClinicSession();
        if (refreshed?.session_token) {
          setClinicSession(refreshed.session_token);
        }
      } catch {
        // refresh is best-effort; fall through to /me
      }
      const data = await getClinicMe();
      setMe(data);
      if (data?.clinic?.clinic_id) {
        setClinicId(data.clinic.clinic_id);
        rememberClinicDemoProfile({
          clinic_id: data.clinic.clinic_id,
          clinic_name: data.clinic.name,
          session_token: getClinicSession()!,
          member_email: data.member?.email,
          role: data.member?.role,
        });
        setDemoProfiles(listClinicDemoProfiles());
      }
      try {
        const a = await fetchClinicAnalytics(30);
        setAnalytics(a);
      } catch {
        setAnalytics(null);
      }
    } catch {
      setMe(null);
      setAnalytics(null);
      setDemoProfiles(listClinicDemoProfiles());
    }
  };

  useEffect(() => {
    trackOncoAgentPageView('/oncoagent/clinic', 'OncoAgent Clinic Org');
    document.title = 'Clinic workspace · OncoAgent';
    setDemoProfiles(listClinicDemoProfiles());

    const ssoStatus = params.get('sso');
    const session = params.get('session');
    const clinicId = params.get('clinic_id');
    const magic = params.get('magic');
    const reset = params.get('reset');
    if (ssoStatus === 'ok' && session) {
      setClinicSession(session);
      if (clinicId) setClinicId(clinicId);
      window.history.replaceState({}, '', '/oncoagent/app/clinic');
    } else if (ssoStatus === 'error') {
      setError(params.get('message') || 'SSO login failed');
    } else if (magic) {
      consumeClinicMagicLink(magic)
        .then(async (res) => {
          setClinicSession(res.session_token);
          if (res.clinic?.clinic_id) setClinicId(res.clinic.clinic_id);
          window.history.replaceState({}, '', '/oncoagent/app/clinic');
          await refresh();
        })
        .catch((e: any) => {
          setError(getErrorMessage(e) || 'Magic link failed');
        });
    } else if (reset) {
      setResetToken(reset);
      setMode('reset');
      window.history.replaceState({}, '', '/oncoagent/app/clinic');
    } else if (params.get('invite')) {
      setMode('join');
      setJoin((prev) => ({ ...prev, invite_code: String(params.get('invite') || '') }));
    } else if (listClinicDemoProfiles().length && !getClinicSession()) {
      setMode('login');
    }
    refresh();
  }, [params]);

  useEffect(() => {
    const email = String(user?.email || '').trim();
    const name = String(user?.full_name || user?.name || '').trim();
    if (email) {
      setLogin((prev) => ({ ...prev, email }));
      setReg((prev) => ({
        ...prev,
        admin_email: email,
        admin_name: prev.admin_name || name,
      }));
      setJoin((prev) => ({
        ...prev,
        email,
        name: prev.name || name,
      }));
    } else if (name) {
      setReg((prev) => (prev.admin_name ? prev : { ...prev, admin_name: name }));
      setJoin((prev) => (prev.name ? prev : { ...prev, name }));
    }
  }, [user]);

  const onLogin = async () => {
    setBusy(true);
    setError(null);
    const issues = clinicLoginIssues(login);
    setFieldErrors(issues as unknown as Record<string, string>);
    if (firstIssue(issues)) {
      setError(firstIssue(issues));
      setBusy(false);
      return;
    }
    try {
      const res = await loginClinic({
        email: login.email,
        password: login.password,
        clinic_id: login.clinic_id || undefined,
        mfa_code: login.mfa_code || undefined,
      });
      if (res.mfa_required && !res.session_token) {
        setMfaNeeded(true);
        setError('Enter the 6-digit code from your authenticator to finish.');
        return;
      }
      setMfaNeeded(false);
      setClinicSession(res.session_token);
      if (res.clinic?.clinic_id) setClinicId(res.clinic.clinic_id);
      trackOncoAgentCTA('clinic_login', 'clinic');
      await refresh();
    } catch (e: any) {
      setError(getErrorMessage(e) || 'Could not sign in to this clinic. Check the work email and password.');
    } finally {
      setBusy(false);
    }
  };

  const onMagicLink = async () => {
    setBusy(true);
    setError(null);
    setMagicNote(null);
    try {
      const res = await requestClinicMagicLink({
        email: login.email,
        clinic_id: login.clinic_id || undefined,
      });
      if (res.dev_token) {
        setMagicNote('Check your work email for a one-time sign-in link from ByOnco.');
      } else {
        setMagicNote(
          res.email_delivery === 'smtp'
            ? 'Check your work email for a one-time sign-in link from ByOnco.'
            : res.email_delivery === 'failed'
              ? 'We could not send the sign-in email just now. Use your password, or try again in a minute.'
              : 'If that work email belongs to a clinic, a sign-in link is ready. Open it on this same ByOnco account.'
        );
      }
      trackOncoAgentCTA('clinic_magic_link', 'clinic');
    } catch (e: any) {
      setError(getErrorMessage(e) || 'Magic link failed');
    } finally {
      setBusy(false);
    }
  };

  const onForgotPassword = async () => {
    setBusy(true);
    setError(null);
    setResetNote(null);
    try {
      const res = await requestClinicPasswordReset({
        email: login.email,
        clinic_id: login.clinic_id || undefined,
      });
      if (res.dev_token) {
        setResetNote('Check your work email for the reset link from ByOnco.');
      } else {
        setResetNote(
          res.email_delivery === 'smtp'
            ? 'Check your work email for the reset link from ByOnco.'
            : res.email_delivery === 'failed'
              ? 'We could not send the reset email just now. Try again, or ask a clinic admin to rotate your access.'
              : 'If that work email belongs to a clinic, a reset link is ready. Open it on this same ByOnco account.'
        );
      }
      trackOncoAgentCTA('clinic_password_reset_request', 'clinic');
    } catch (e: any) {
      setError(getErrorMessage(e) || 'Reset request failed');
    } finally {
      setBusy(false);
    }
  };

  const onResetPassword = async () => {
    if (!resetToken || resetPassword.length < 8) return;
    setBusy(true);
    setError(null);
    try {
      const res = await resetClinicPassword(resetToken, resetPassword);
      setClinicSession(res.session_token);
      if (res.clinic?.clinic_id) setClinicId(res.clinic.clinic_id);
      setMode('login');
      setResetToken('');
      setResetPassword('');
      trackOncoAgentCTA('clinic_password_reset', 'clinic');
      await refresh();
    } catch (e: any) {
      setError(getErrorMessage(e) || 'Password reset failed');
    } finally {
      setBusy(false);
    }
  };

  const onSwitchDemoClinic = async (clinicId: string) => {
    const hit = switchClinicDemoProfile(clinicId);
    if (!hit) return;
    setBusy(true);
    setError(null);
    try {
      await refresh();
      trackOncoAgentCTA('clinic_demo_switch', 'clinic');
    } finally {
      setBusy(false);
    }
  };

  const onLookupMemberships = async () => {
    if (!login.email.trim()) return;
    try {
      const res = await lookupClinicMemberships(login.email.trim());
      setMemberships(res.items || []);
    } catch {
      setMemberships([]);
    }
  };

  const onRegister = async () => {
    setBusy(true);
    setError(null);
    const payload = {
      ...reg,
      admin_email: String(user?.email || reg.admin_email).trim(),
      admin_name: reg.admin_name.trim() || String(user?.full_name || user?.name || '').trim(),
    };
    const issues = clinicRegisterIssues(payload);
    setFieldErrors(issues as unknown as Record<string, string>);
    if (firstIssue(issues)) {
      setError(firstIssue(issues));
      setBusy(false);
      return;
    }
    try {
      const res = await registerClinic({
        ...payload,
        network_id: payload.network_id || undefined,
        password: payload.password,
        accepts_referrals: payload.mode !== 'referring',
      });
      setClinicSession(res.session_token);
      if (res.clinic?.clinic_id) setClinicId(res.clinic.clinic_id);
      setApiKeyOnce(res.api_key);
      trackOncoAgentCTA('clinic_register', 'clinic');
      await refresh();
    } catch (e: any) {
      setError(getErrorMessage(e) || 'Could not create the clinic. Try a longer clinic name and a work email.');
    } finally {
      setBusy(false);
    }
  };

  const onJoin = async () => {
    setBusy(true);
    setError(null);
    const payload = {
      ...join,
      email: String(user?.email || join.email).trim(),
    };
    const issues = clinicJoinIssues(payload);
    setFieldErrors(issues as unknown as Record<string, string>);
    if (firstIssue(issues)) {
      setError(firstIssue(issues));
      setBusy(false);
      return;
    }
    try {
      const res = await joinClinic({
        ...payload,
        password: payload.password,
      });
      setClinicSession(res.session_token);
      if (res.clinic?.clinic_id) setClinicId(res.clinic.clinic_id);
      trackOncoAgentCTA('clinic_join', 'clinic');
      await refresh();
    } catch (e: any) {
      setError(getErrorMessage(e) || 'Could not join that clinic. Check the invite code.');
    } finally {
      setBusy(false);
    }
  };

  const onLogout = async () => {
    await logoutClinic();
    setMe(null);
    setApiKeyOnce(null);
  };

  const isAdmin = me?.permissions?.is_admin || me?.member?.role === 'admin';
  const signedInEmail = String(user?.email || '').trim();
  const registerIssuesLive = clinicRegisterIssues({
    ...reg,
    admin_email: signedInEmail || reg.admin_email,
  });
  const joinIssuesLive = clinicJoinIssues({
    ...join,
    email: signedInEmail || join.email,
  });
  const loginIssuesLive = clinicLoginIssues(login);

  return (
    <OaWorkspaceShell
      kicker="Your practice"
      title={me?.clinic ? me.clinic.name : 'Set up this clinic'}
      subtitle={
        me?.clinic
          ? `${[me.clinic.city, me.clinic.state].filter(Boolean).join(', ') || 'Your practice'} · Colleagues join from an invite you send.`
          : 'Create this practice so referrals and inbox have a home, or join one a colleague already opened.'
      }
    >
      {error ? (
        <div className="oa-ws-error" role="alert">
          {error}
        </div>
      ) : null}

      {me?.clinic ? (
        <ClinicPracticeHome
          me={me}
          analytics={analytics}
          isAdmin={isAdmin}
          busy={busy}
          setBusy={setBusy}
          setError={setError}
          refresh={refresh}
          apiKeyOnce={apiKeyOnce}
          setApiKeyOnce={setApiKeyOnce}
          inviteEmail={inviteEmail}
          setInviteEmail={setInviteEmail}
          inviteNote={inviteNote}
          setInviteNote={setInviteNote}
          newPassword={newPassword}
          setNewPassword={setNewPassword}
          demoProfiles={demoProfiles}
          onSwitchDemoClinic={onSwitchDemoClinic}
          onLogout={onLogout}
        />
      ) : (
        <div className="oa-ws-panel">
          <p className="oa-ws-muted" style={{ marginBottom: '1rem' }}>
            ByOnco sign-in is you. This screen is the <strong>practice</strong> that owns referrals and inbox.
          </p>
          <div className="oa-ws-chip-row" style={{ marginBottom: '1rem' }}>
            <button
              type="button"
              className={`oa-ws-chip ${mode === 'register' ? 'on' : ''}`}
              onClick={() => {
                setMode('register');
                setError(null);
                setFieldErrors({});
              }}
            >
              Create clinic
            </button>
            <button
              type="button"
              className={`oa-ws-chip ${mode === 'join' ? 'on' : ''}`}
              onClick={() => {
                setMode('join');
                setError(null);
                setFieldErrors({});
              }}
            >
              Join with invite
            </button>
            <button
              type="button"
              className={`oa-ws-chip ${mode === 'login' || mode === 'forgot' || mode === 'reset' ? 'on' : ''}`}
              onClick={() => {
                setMode('login');
                setError(null);
                setFieldErrors({});
              }}
            >
              Returning clinician
            </button>
          </div>

          {demoProfiles.length > 0 && (mode === 'login' || mode === 'register') ? (
            <div style={{ marginBottom: '1rem' }}>
              <p className="oa-ws-muted">Practices already opened on this computer</p>
              <div className="oa-ws-actions">
                {demoProfiles.map((p) => (
                  <button
                    key={p.clinic_id}
                    type="button"
                    className="oa-ws-btn oa-ws-btn-secondary"
                    disabled={busy}
                    onClick={() => onSwitchDemoClinic(p.clinic_id)}
                  >
                    Open {p.clinic_name || 'clinic'}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {mode === 'login' ? (
            <div className="oa-ws-grid cols-2">
              <div>
                <label className="oa-ws-label">Work email</label>
                <input
                  className={`oa-ws-input ${fieldErrors.email ? 'invalid' : ''}`}
                  value={login.email}
                  onChange={(e) => {
                    setLogin({ ...login, email: e.target.value });
                    setFieldErrors((prev) => ({ ...prev, email: '' }));
                  }}
                  onBlur={onLookupMemberships}
                  placeholder="you@clinic.org"
                  autoComplete="username"
                  readOnly={Boolean(signedInEmail)}
                />
                {signedInEmail ? (
                  <p className="oa-ws-hint">Using your ByOnco sign-in email.</p>
                ) : null}
                {fieldErrors.email ? <p className="oa-ws-field-error">{fieldErrors.email}</p> : null}
              </div>
              <div>
                <label className="oa-ws-label">Password</label>
                <input
                  className={`oa-ws-input ${fieldErrors.password ? 'invalid' : ''}`}
                  type="password"
                  value={login.password}
                  onChange={(e) => {
                    setLogin({ ...login, password: e.target.value });
                    setFieldErrors((prev) => ({ ...prev, password: '' }));
                  }}
                  autoComplete="current-password"
                />
                {fieldErrors.password ? <p className="oa-ws-field-error">{fieldErrors.password}</p> : null}
              </div>
              {mfaNeeded ? (
              <div>
                <label className="oa-ws-label">Authenticator code</label>
                <input
                  className="oa-ws-input"
                  value={login.mfa_code}
                  onChange={(e) => setLogin({ ...login, mfa_code: e.target.value })}
                  placeholder="123456"
                  autoComplete="one-time-code"
                />
              </div>
              ) : null}
              {memberships.length > 1 ? (
                <div style={{ gridColumn: '1 / -1' }}>
                  <label className="oa-ws-label">Clinic</label>
                  <select
                    className="oa-ws-select"
                    value={login.clinic_id}
                    onChange={(e) => setLogin({ ...login, clinic_id: e.target.value })}
                  >
                    <option value="">Select clinic</option>
                    {memberships.map((m) => (
                      <option key={m.clinic_id} value={m.clinic_id}>
                        {m.clinic_name || m.clinic_id} · {m.role}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              <div className="oa-ws-actions" style={{ gridColumn: '1 / -1' }}>
                <button
                  type="button"
                  className="oa-ws-btn oa-ws-btn-primary"
                  disabled={busy || Boolean(firstIssue(loginIssuesLive))}
                  onClick={onLogin}
                >
                  Sign in
                </button>
                <button
                  type="button"
                  className="oa-ws-btn oa-ws-btn-ghost"
                  disabled={busy || !login.email}
                  onClick={onMagicLink}
                >
                  Email me a sign-in link
                </button>
                <button
                  type="button"
                  className="oa-ws-btn oa-ws-btn-ghost"
                  onClick={() => setMode('forgot')}
                >
                  Forgot password
                </button>
              </div>
              {magicNote ? <p className="oa-ws-muted" style={{ gridColumn: '1 / -1' }}>{magicNote}</p> : null}
            </div>
          ) : null}

          {mode === 'forgot' ? (
            <div className="oa-ws-grid cols-2">
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="oa-ws-label">Work email</label>
                <input
                  className="oa-ws-input"
                  value={login.email}
                  onChange={(e) => setLogin({ ...login, email: e.target.value })}
                  placeholder="you@clinic.org"
                />
              </div>
              <div className="oa-ws-actions" style={{ gridColumn: '1 / -1' }}>
                <button
                  type="button"
                  className="oa-ws-btn oa-ws-btn-primary"
                  disabled={busy || !login.email}
                  onClick={onForgotPassword}
                >
                  Send reset link
                </button>
                <button type="button" className="oa-ws-btn oa-ws-btn-ghost" onClick={() => setMode('login')}>
                  Back to sign in
                </button>
              </div>
              {resetNote ? <p className="oa-ws-muted" style={{ gridColumn: '1 / -1' }}>{resetNote}</p> : null}
            </div>
          ) : null}

          {mode === 'reset' ? (
            <div className="oa-ws-grid cols-2">
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="oa-ws-label">New password</label>
                <input
                  className="oa-ws-input"
                  type="password"
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  placeholder="Mixed case + number, min 8"
                />
              </div>
              <div className="oa-ws-actions" style={{ gridColumn: '1 / -1' }}>
                <button
                  type="button"
                  className="oa-ws-btn oa-ws-btn-primary"
                  disabled={busy || !resetToken || resetPassword.length < 8}
                  onClick={onResetPassword}
                >
                  Set new password
                </button>
              </div>
              {resetNote ? <p className="oa-ws-muted" style={{ gridColumn: '1 / -1' }}>{resetNote}</p> : null}
            </div>
          ) : null}

          {mode === 'register' ? (
            <div className="oa-ws-grid cols-2">
              <div>
                <label className="oa-ws-label">Clinic name</label>
                <input
                  className={`oa-ws-input ${fieldErrors.name ? 'invalid' : ''}`}
                  value={reg.name}
                  onChange={(e) => {
                    setReg({ ...reg, name: e.target.value });
                    setFieldErrors((prev) => ({ ...prev, name: '' }));
                  }}
                  placeholder="Riverside Family Medicine"
                />
                <p className="oa-ws-hint">At least 2 characters. This is what colleagues will see.</p>
                {fieldErrors.name ? <p className="oa-ws-field-error">{fieldErrors.name}</p> : null}
              </div>
              <div>
                <label className="oa-ws-label">Your name</label>
                <input
                  className={`oa-ws-input ${fieldErrors.admin_name ? 'invalid' : ''}`}
                  value={reg.admin_name}
                  onChange={(e) => {
                    setReg({ ...reg, admin_name: e.target.value });
                    setFieldErrors((prev) => ({ ...prev, admin_name: '' }));
                  }}
                  placeholder="Dr Jane Smith"
                />
                {fieldErrors.admin_name ? (
                  <p className="oa-ws-field-error">{fieldErrors.admin_name}</p>
                ) : null}
              </div>
              <div>
                <label className="oa-ws-label">Work email</label>
                <input
                  className={`oa-ws-input ${fieldErrors.admin_email ? 'invalid' : ''}`}
                  value={signedInEmail || reg.admin_email}
                  onChange={(e) => {
                    if (signedInEmail) return;
                    setReg({ ...reg, admin_email: e.target.value });
                    setFieldErrors((prev) => ({ ...prev, admin_email: '' }));
                  }}
                  readOnly={Boolean(signedInEmail)}
                  autoComplete="email"
                  placeholder="you@clinic.org"
                />
                <p className="oa-ws-hint">
                  {signedInEmail
                    ? 'This is the email on your ByOnco account.'
                    : 'Use a real work email.'}
                </p>
                {fieldErrors.admin_email ? (
                  <p className="oa-ws-field-error">{fieldErrors.admin_email}</p>
                ) : null}
              </div>
              <div>
                <label className="oa-ws-label">Password</label>
                <input
                  className={`oa-ws-input ${fieldErrors.password ? 'invalid' : ''}`}
                  type="password"
                  value={reg.password}
                  onChange={(e) => {
                    setReg({ ...reg, password: e.target.value });
                    setFieldErrors((prev) => ({ ...prev, password: '' }));
                  }}
                  placeholder="ClinicPass1"
                  autoComplete="new-password"
                />
                <p className="oa-ws-hint">8+ characters, mixed case, and a number. You will use this to return.</p>
                {fieldErrors.password ? <p className="oa-ws-field-error">{fieldErrors.password}</p> : null}
              </div>
              <div>
                <label className="oa-ws-label">City / State</label>
                <div className="oa-ws-grid cols-2">
                  <input
                    className="oa-ws-input"
                    placeholder="City"
                    value={reg.city}
                    onChange={(e) => setReg({ ...reg, city: e.target.value })}
                  />
                  <input
                    className="oa-ws-input"
                    placeholder="State"
                    value={reg.state}
                    onChange={(e) => setReg({ ...reg, state: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="oa-ws-label">Clinic mode</label>
                <select
                  className="oa-ws-select"
                  value={reg.mode}
                  onChange={(e) => setReg({ ...reg, mode: e.target.value as any })}
                >
                  <option value="both">Send and receive referrals</option>
                  <option value="referring">Send only</option>
                  <option value="receiving">Receive only</option>
                </select>
              </div>
              <div>
                <label className="oa-ws-label">Shared group name (optional)</label>
                <input
                  className="oa-ws-input"
                  value={reg.network_id}
                  onChange={(e) => setReg({ ...reg, network_id: e.target.value })}
                  placeholder="If several clinics share patients"
                />
              </div>
              <div className="oa-ws-actions">
                <button
                  type="button"
                  className="oa-ws-btn oa-ws-btn-primary"
                  disabled={busy || Boolean(firstIssue(registerIssuesLive))}
                  onClick={onRegister}
                >
                  {busy ? 'Creating…' : 'Create clinic'}
                </button>
              </div>
              {firstIssue(registerIssuesLive) ? (
                <p className="oa-ws-hint" style={{ gridColumn: '1 / -1' }}>
                  Create turns on when the clinic name is at least 2 characters and the password has 8+ mixed-case characters and a number.
                </p>
              ) : null}
            </div>
          ) : null}

          {mode === 'join' ? (
            <div className="oa-ws-grid cols-2">
              <div>
                <label className="oa-ws-label">Invite code</label>
                <input
                  className={`oa-ws-input ${fieldErrors.invite_code ? 'invalid' : ''}`}
                  value={join.invite_code}
                  onChange={(e) => {
                    setJoin({ ...join, invite_code: e.target.value });
                    setFieldErrors((prev) => ({ ...prev, invite_code: '' }));
                  }}
                  placeholder="From your clinic admin"
                />
                {fieldErrors.invite_code ? (
                  <p className="oa-ws-field-error">{fieldErrors.invite_code}</p>
                ) : null}
              </div>
              <div>
                <label className="oa-ws-label">Your name</label>
                <input
                  className={`oa-ws-input ${fieldErrors.name ? 'invalid' : ''}`}
                  value={join.name}
                  onChange={(e) => {
                    setJoin({ ...join, name: e.target.value });
                    setFieldErrors((prev) => ({ ...prev, name: '' }));
                  }}
                />
                {fieldErrors.name ? <p className="oa-ws-field-error">{fieldErrors.name}</p> : null}
              </div>
              <div>
                <label className="oa-ws-label">Work email</label>
                <input
                  className={`oa-ws-input ${fieldErrors.email ? 'invalid' : ''}`}
                  value={signedInEmail || join.email}
                  onChange={(e) => {
                    if (signedInEmail) return;
                    setJoin({ ...join, email: e.target.value });
                    setFieldErrors((prev) => ({ ...prev, email: '' }));
                  }}
                  readOnly={Boolean(signedInEmail)}
                  autoComplete="email"
                />
                {signedInEmail ? (
                  <p className="oa-ws-hint">Using your ByOnco sign-in email.</p>
                ) : null}
                {fieldErrors.email ? <p className="oa-ws-field-error">{fieldErrors.email}</p> : null}
              </div>
              <div>
                <label className="oa-ws-label">Password</label>
                <input
                  className={`oa-ws-input ${fieldErrors.password ? 'invalid' : ''}`}
                  type="password"
                  value={join.password}
                  onChange={(e) => {
                    setJoin({ ...join, password: e.target.value });
                    setFieldErrors((prev) => ({ ...prev, password: '' }));
                  }}
                  placeholder="8+ characters, mixed case, a number"
                  autoComplete="new-password"
                />
                {fieldErrors.password ? <p className="oa-ws-field-error">{fieldErrors.password}</p> : null}
              </div>
              <div>
                <label className="oa-ws-label">Role</label>
                <select
                  className="oa-ws-select"
                  value={join.role}
                  onChange={(e) => setJoin({ ...join, role: e.target.value })}
                >
                  <option value="clinician">Clinician</option>
                  <option value="navigator">Navigator</option>
                  <option value="oncologist">Oncologist</option>
                </select>
              </div>
              <div className="oa-ws-actions">
                <button
                  type="button"
                  className="oa-ws-btn oa-ws-btn-primary"
                  disabled={busy || Boolean(firstIssue(joinIssuesLive))}
                  onClick={onJoin}
                >
                  {busy ? 'Joining…' : 'Join clinic'}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </OaWorkspaceShell>
  );
}

export default OncoAgentClinic;
